// Angular
import {Injectable} from '@angular/core';
// RxJS
import {of, forkJoin} from 'rxjs';
import {mergeMap, map, withLatestFrom, filter, tap} from 'rxjs/operators';
// NGRX
import {Effect, Actions, ofType} from '@ngrx/effects';
import {Store, select} from '@ngrx/store';
// CRUD
import {QueryParamsModel} from '../../_base/crud';
// Services
import {BaseOfValuesService} from '../_services/base-of-value.service';
// State
import {AppState} from '../../reducers';
// Selectors
import {allBaseOfValuesLoaded} from '../_selectors/base-of-value.selector';
// Actions
import {
    AllBaseOfValuesLoaded,
    AllBaseOfValuesRequested,
    BaseOfValueActionTypes,
    BaseOfValuesPageRequested,
    BaseOfValuesPageLoaded,
    BaseOfValueUpdated,
    BaseOfValuesPageToggleLoading,
    BaseOfValueDeleted,
    BaseOfValueOnServerCreated,
    BaseOfValueCreated,
    BaseOfValueActionToggleLoading, BaseOfValueTrashFlushed, BaseOfValueOnServerRestored, BaseOfValueRestored,
    BaseOfValueOnServerAdminRestored, BaseOfValueAdminRestored, BaseOfValueDeletedFromTrash, BaseOfValueDeletedFromAdminTrash
} from '../_actions/base-of-value.actions';

@Injectable()
export class BaseOfValueEffects {
    showPageLoadingDispatcher = new BaseOfValuesPageToggleLoading({isLoading: true});
    showActionLoadingDispatcher = new BaseOfValueActionToggleLoading({isLoading: true});
    hideActionLoadingDispatcher = new BaseOfValueActionToggleLoading({isLoading: false});

    @Effect()
    loadAllBaseOfValue$ = this.actions$
        .pipe(
            ofType<AllBaseOfValuesRequested>(BaseOfValueActionTypes.AllBaseOfValuesRequested),
            withLatestFrom(this.store.pipe(select(allBaseOfValuesLoaded))),
            filter(([action, isAllBaseOfValueLoaded]) => !isAllBaseOfValueLoaded),
            mergeMap(() => {
                return this.service.getAll()
            }),
            map(res => {
                return new AllBaseOfValuesLoaded({items: res.data});
            })
        );

    @Effect()
    loadBaseOfValuePage$ = this.actions$
        .pipe(
            ofType<BaseOfValuesPageRequested>(BaseOfValueActionTypes.BaseOfValuesPageRequested),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showPageLoadingDispatcher);
                const requestToServer = this.service.fetch(payload.page);
                const lastQuery = of(payload.page);
                return forkJoin([requestToServer, lastQuery]);
            }),
            map(response => {
                const result: any = response[0];
                const lastQuery: QueryParamsModel = response[1];
                return new BaseOfValuesPageLoaded({
                    items: result.data,
                    totalCount: result.pagination.total,
                    totalTrashed: result.pagination.total_trashed,
                    totalAdminTrashed: result.pagination.admin_trashed,
                    page: lastQuery
                });
            }),
        );

    @Effect()
    updateBaseOfValue$ = this.actions$
        .pipe(
            ofType<BaseOfValueUpdated>(BaseOfValueActionTypes.BaseOfValueUpdated),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.update(payload.item);
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );


    @Effect()
    createBaseOfValue$ = this.actions$
        .pipe(
            ofType<BaseOfValueOnServerCreated>(BaseOfValueActionTypes.BaseOfValueOnServerCreated),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.create(payload.item).pipe(
                    tap(res => {
                        this.store.dispatch(new BaseOfValueCreated({item: res.data}));
                    })
                );
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    // trash system


    @Effect()
    flushBaseOfValueTrash$ = this.actions$
        .pipe(
            ofType<BaseOfValueTrashFlushed>(BaseOfValueActionTypes.BaseOfValueTrashFlushed),
            mergeMap(() => {
                    return this.service.flushTrash();
                }
            ),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    restoreBaseOfValue$ = this.actions$
        .pipe(
            ofType<BaseOfValueOnServerRestored>(BaseOfValueActionTypes.BaseOfValueOnServerRestored),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.restoreFromTrash(payload.id).pipe(
                    tap(res => {
                        this.store.dispatch(new BaseOfValueRestored({item: res.data}));
                    })
                );
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    restoreAdminBaseOfValue$ = this.actions$
        .pipe(
            ofType<BaseOfValueOnServerAdminRestored>(BaseOfValueActionTypes.BaseOfValueOnServerAdminRestored),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.restoreFromTrash(payload.id).pipe(
                    tap(res => {
                        this.store.dispatch(new BaseOfValueAdminRestored({item: res.data}));
                    })
                );
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    deleteBaseOfValue$ = this.actions$
        .pipe(
            ofType<BaseOfValueDeleted>(BaseOfValueActionTypes.BaseOfValueDeleted),
            mergeMap(({payload}) => {
                    this.store.dispatch(this.showActionLoadingDispatcher);
                    return this.service.delete(payload.id);
                }
            ),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    deleteFromTrashBaseOfValue$ = this.actions$
        .pipe(
            ofType<BaseOfValueDeletedFromTrash>(BaseOfValueActionTypes.BaseOfValueDeletedFromTrash),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.deleteFromTrash(payload.id);
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    @Effect()
    deleteFromAdminTrashBaseOfValue$ = this.actions$
        .pipe(
            ofType<BaseOfValueDeletedFromAdminTrash>(BaseOfValueActionTypes.BaseOfValueDeletedFromAdminTrash),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                return this.service.deleteFromAdminTrash(payload.id);
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            }),
        );

    constructor(private actions$: Actions, private service: BaseOfValuesService, private store: Store<AppState>) {
    }
}
